import {
  Dispatch,
  FocusEventHandler,
  Fragment,
  KeyboardEventHandler,
  RefObject,
  SetStateAction,
  useEffect,
  useRef,
} from 'react'

import { ensuredForwardRef } from 'react-use'
import { styled, css } from 'styled-components'

import Icon from 'core/components/Icon'
import { variables } from 'core/styles'

import { BorrowerSearchInputChangeHandler } from './BorrowerSearchInputClearButton'

export type SearchPart = {
  key: string
  value: string
  valid: boolean
}

export type BorrowerSearchInputSubmitHandler = () => void
export type BorrowerSearchInputOptimizer = () => void
export type BorrowerSearchInputColonValidator = (index: number) => boolean

type BorrowerSearchInputProps = {
  inputHeight: string
  isOptimized: boolean
  setInputHeight: Dispatch<SetStateAction<number>>
  searchInputValue: string
  searchParts: Array<SearchPart>
  handleBlur: FocusEventHandler<HTMLTextAreaElement>
  isColonValidAtIndex: BorrowerSearchInputColonValidator
  onChangeSearchInput: BorrowerSearchInputChangeHandler
  optimizeSearchInput: BorrowerSearchInputOptimizer
  onSubmitSearchInput: BorrowerSearchInputSubmitHandler
  setIsToolbarVisible: Dispatch<SetStateAction<boolean>>
  setFocusOnToolbar: () => void
}

const BorrowerSearchInput = ensuredForwardRef<
  HTMLTextAreaElement,
  BorrowerSearchInputProps
>(
  (
    {
      inputHeight,
      isOptimized,
      setInputHeight,
      searchInputValue,
      searchParts,
      handleBlur,
      isColonValidAtIndex,
      onChangeSearchInput,
      optimizeSearchInput,
      onSubmitSearchInput,
      setIsToolbarVisible,
      setFocusOnToolbar,
    },
    ref,
  ) => {
    const overlayRef = useRef<HTMLDivElement>(null)
    // ensuredForwardRef guarantees ref will be a RefObject but is typed wrong
    const current = (ref as RefObject<HTMLTextAreaElement | null>)?.current

    const handleInputKeydown: KeyboardEventHandler<HTMLTextAreaElement> = (
      e,
    ) => {
      switch (e.key) {
        case 'ArrowDown':
          setFocusOnToolbar()
          break
        case 'Escape':
          current?.blur()
          break
        case 'Enter':
          // disallow linebreaks
          e.preventDefault()
          if (e.shiftKey && !isOptimized) {
            optimizeSearchInput()
          } else {
            onSubmitSearchInput()
          }
          current?.blur()
          break
        case `'`:
          // disallow single quotes
          e.preventDefault()
          break
        case ':':
          if (current && !isColonValidAtIndex(current.selectionStart)) {
            // disallow colons when illegal
            e.preventDefault()
          }
      }
    }

    useEffect(() => {
      // sync input height with overlay height
      if (overlayRef.current) setInputHeight(overlayRef.current.clientHeight)
    }, [searchInputValue, setInputHeight])

    return (
      <>
        <SearchPartsOverlay ref={overlayRef}>
          {searchInputValue ? (
            searchParts?.map(({ key, value, valid }, index) => {
              if (key === '' && (value === '' || !/\S/.test(value))) {
                return (
                  <Fragment key={`searchPart-${index}`}>{value + ' '}</Fragment>
                )
              }
              return (
                <SearchPartSpan
                  key={`searchPart-${index}`}
                  variant={key === '' ? 'base' : valid ? 'valid' : 'invalid'}
                >
                  {key === '' ? `${value}` : `${key}:${value}`}
                </SearchPartSpan>
              )
            })
          ) : (
            <SearchPlaceholder>
              Search by first name, last name, or street address
            </SearchPlaceholder>
          )}
        </SearchPartsOverlay>
        <SearchLabel aria-label='Search borrowers' htmlFor='borrower-search'>
          <Icon color={variables.colorBlack40} fontSize='24px' name='search' />
        </SearchLabel>
        <StyledTextInput
          height={inputHeight}
          id='borrower-search'
          onBlur={handleBlur}
          onChange={(e) => onChangeSearchInput(e.target.value)}
          onFocus={() => setIsToolbarVisible(true)}
          onKeyDown={handleInputKeydown}
          onPaste={(e) => {
            e.preventDefault()
            const { selectionStart = 0, selectionEnd = 0 } = current ?? {}
            const pastedText = e.clipboardData.getData('text/plain').trim()
            onChangeSearchInput(
              searchInputValue.substring(0, selectionStart) +
                pastedText +
                searchInputValue.substring(selectionEnd),
            )
            const newSelectionStart = selectionStart + pastedText.length
            setTimeout(
              () =>
                current?.setSelectionRange(
                  newSelectionStart,
                  newSelectionStart,
                ),
              1,
            )
          }}
          ref={ref}
          rows={1}
          spellCheck={false}
          value={searchInputValue}
        />
      </>
    )
  },
)

export default BorrowerSearchInput

export const BorrowerSearchInputWrapper = styled.div`
  position: relative;
  border-bottom: 2px solid ${(p) => p.theme.border};
  height: 40px;
`
const SearchLabel = styled.label`
  position: absolute;
  top: 8px;
  left: 16px;
`
const inputTextStyles = css`
  line-height: 24px;
  font-size: 16px;
`
const inputWrapStyles = css`
  white-space: pre-wrap;
  word-break: break-word;
`
const StyledTextInput = styled.textarea<{ height: string }>`
  ${inputTextStyles};
  ${inputWrapStyles};

  box-sizing: border-box;
  position: relative;
  z-index: 1;
  margin-bottom: -8px;
  outline: none;
  border: none;
  background: transparent;
  padding: 8px 0 8px 50px;
  width: calc(100% - 50px);
  height: 40px;
  resize: none;
  color: transparent;
  caret-color: ${(p) => p.theme.text};
`
const SearchPartsOverlay = styled.div`
  ${inputTextStyles};
  ${inputWrapStyles};

  position: absolute;
  top: 8px;
  right: 50px;
  left: 50px;
  min-height: 24px;
  color: ${(p) => p.theme.labelText};
  pointer-events: none;
  user-select: none;
`
const SearchPlaceholder = styled.span`
  ${inputTextStyles};

  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${(p) => p.theme.labelText};
`
const SearchPartSpan = styled.span<{ variant: 'base' | 'valid' | 'invalid' }>`
  ${inputTextStyles};
  ${inputWrapStyles};

  margin: 0 -1px;
  border: 1px solid transparent;
  border-radius: 4px;
  padding: 0;

  ${(p) =>
    p.variant === 'valid' &&
    `
    background: rgba(216, 238, 252, 0.2);
    border: 1px solid ${p.theme.highlight};
  `}
  ${(p) =>
    p.variant === 'invalid' &&
    `
    background: ${variables.colorOrangeLighten};
    border: 1px solid ${variables.colorOrange};
  `}
`
